import { RefObject, useEffect, useRef } from 'react';

interface UseInfiniteScrollSentinelParams {
  enabled: boolean;
  onIntersect: () => void;
  /**
   * Contenedor scrolleable que recorta al centinela. Se usa como `root` del
   * observer: con `root: null` (viewport) el `rootMargin` no expande el recorte
   * del contenedor, así que no hay anticipación y el usuario llega al fondo y
   * recién ahí empieza a esperar.
   */
  rootRef?: RefObject<Element | null>;
}

/**
 * Sin `IntersectionObserver` no hay carga automática al scrollear, así que la
 * columna necesita ofrecer un control explícito para traer la siguiente tanda.
 */
export const supportsIntersectionObserver = () =>
  typeof IntersectionObserver !== 'undefined';

/** Cuánto antes del fondo se dispara la carga de la tanda siguiente. */
const PREFETCH_MARGIN = '300px';

/**
 * Observa un elemento centinela y dispara `onIntersect` cuando entra en vista.
 * Se usa para cargar la siguiente tanda de tarjetas al llegar al fondo de una
 * columna.
 */
export const useInfiniteScrollSentinel = <T extends Element>({
  enabled,
  onIntersect,
  rootRef,
}: UseInfiniteScrollSentinelParams) => {
  const sentinelRef = useRef<T | null>(null);
  const onIntersectRef = useRef(onIntersect);
  onIntersectRef.current = onIntersect;

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!enabled || !sentinel || !supportsIntersectionObserver()) {
      return;
    }

    let observer: IntersectionObserver;

    try {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            onIntersectRef.current();
          }
        },
        {
          // `root` sólo es válido si es un ancestro del centinela. Si el ref
          // todavía no está montado se cae al viewport, que funciona igual
          // aunque sin anticipación.
          root: rootRef?.current ?? null,
          rootMargin: PREFETCH_MARGIN,
        },
      );

      observer.observe(sentinel);
    } catch {
      // Un observer mal configurado no puede dejar la columna sin forma de
      // seguir cargando: se ignora y queda el botón manual como salida.
      return;
    }

    return () => observer.disconnect();
    // `rootRef` es estable (viene de useRef); se lo omite a propósito para no
    // recrear el observer en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return sentinelRef;
};
